import { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BACK_IMAGE_URL = `${API_URL}/api/static/business_card_back.jpg`;

// Business card at 3.5" x 2" at 300 DPI
const CARD_W = 1050;
const CARD_H = 600;
const PREVIEW_SCALE = 0.43;

const FONT = 'Helvetica, Arial, sans-serif';

function drawFrontCard(canvas, data) {
  const ctx = canvas.getContext('2d');
  canvas.width = CARD_W;
  canvas.height = CARD_H;

  // White background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  const pad = 60;
  let y = pad + 10;

  // Name - bold, large, navy
  ctx.fillStyle = '#1B365D';
  ctx.font = `800 78px ${FONT}`;
  ctx.textBaseline = 'top';
  ctx.fillText(data.firstName || 'First', pad, y);
  y += 82;
  ctx.fillText(data.lastName || 'Last', pad, y);
  y += 95;

  // Title - medium, steel blue
  ctx.fillStyle = '#5A7FA8';
  ctx.font = `500 30px ${FONT}`;
  ctx.fillText(data.title || 'Sales Representative', pad, y);
  y += 38;

  // Subtitle - regular, gray
  if (data.subtitle) {
    ctx.fillStyle = '#9CA3AF';
    ctx.font = `400 30px ${FONT}`;
    ctx.fillText(data.subtitle, pad, y);
  }

  // Bottom section - contact info left, address right
  const bottomY = CARD_H - pad - 50;

  ctx.fillStyle = '#6B7280';
  ctx.font = `400 22px ${FONT}`;
  ctx.textBaseline = 'top';

  // Left: phone + email
  ctx.textAlign = 'left';
  ctx.fillText(data.phone || '(000) 000-0000', pad, bottomY);
  ctx.fillText(data.email || 'name@ehiehr.com', pad, bottomY + 30);

  // Right: address
  ctx.textAlign = 'right';
  ctx.fillText('100 Eagle Rock Avenue, Suite 304', CARD_W - pad, bottomY);
  ctx.fillText('East Hanover, NJ 07936', CARD_W - pad, bottomY + 30);

  ctx.textAlign = 'left';
}

function downloadCanvas(canvas, filename) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

// Preview component (HTML-based for live updates)
const CardFrontPreview = ({ data }) => (
  <div
    style={{ width: CARD_W, height: CARD_H, fontFamily: FONT, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left' }}
    className="bg-white relative overflow-hidden flex-shrink-0"
  >
    <div style={{ position: 'absolute', inset: 0, padding: 60, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: 78, lineHeight: '82px', fontWeight: 800, color: '#1B365D', margin: 0 }}>
          {data.firstName || 'First'}
        </p>
        <p style={{ fontSize: 78, lineHeight: '82px', fontWeight: 800, color: '#1B365D', margin: 0 }}>
          {data.lastName || 'Last'}
        </p>
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 30, fontWeight: 500, color: '#5A7FA8', lineHeight: '38px', margin: 0 }}>
            {data.title || 'Sales Representative'}
          </p>
          {data.subtitle && (
            <p style={{ fontSize: 30, fontWeight: 400, color: '#9CA3AF', lineHeight: '38px', margin: 0 }}>
              {data.subtitle}
            </p>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <p style={{ fontSize: 22, color: '#6B7280', lineHeight: '30px', margin: 0 }}>{data.phone || '(000) 000-0000'}</p>
          <p style={{ fontSize: 22, color: '#6B7280', lineHeight: '30px', margin: 0 }}>{data.email || 'name@ehiehr.com'}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 22, color: '#6B7280', lineHeight: '30px', margin: 0 }}>100 Eagle Rock Avenue, Suite 304</p>
          <p style={{ fontSize: 22, color: '#6B7280', lineHeight: '30px', margin: 0 }}>East Hanover, NJ 07936</p>
        </div>
      </div>
    </div>
  </div>
);

const CardBackPreview = () => (
  <div style={{ width: CARD_W, height: CARD_H, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left' }} className="overflow-hidden flex-shrink-0">
    <img src={BACK_IMAGE_URL} alt="Card Back" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
  </div>
);

export const BusinessCardGenerator = ({ open, onOpenChange }) => {
  const [data, setData] = useState({ firstName: '', lastName: '', title: 'Sales Representative', subtitle: '', phone: '', email: '' });
  const [downloading, setDownloading] = useState(false);
  const frontCanvasRef = useRef(null);
  const backImageRef = useRef(null);

  const update = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const downloadFront = useCallback(() => {
    const canvas = document.createElement('canvas');
    drawFrontCard(canvas, data);
    const name = `${data.firstName || 'card'}_${data.lastName || ''}`.trim().replace(/\s+/g, '_');
    downloadCanvas(canvas, `business-card-front-${name}.png`);
  }, [data]);

  const downloadBack = useCallback(async () => {
    setDownloading(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = BACK_IMAGE_URL;
      });
      const canvas = document.createElement('canvas');
      canvas.width = CARD_W;
      canvas.height = CARD_H;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, CARD_W, CARD_H);
      downloadCanvas(canvas, 'business-card-back.png');
    } catch (e) {
      // Fallback: open image in new tab for saving
      window.open(BACK_IMAGE_URL, '_blank');
    }
    setDownloading(false);
  }, []);

  const downloadBoth = useCallback(async () => {
    setDownloading(true);
    downloadFront();
    await new Promise(r => setTimeout(r, 500));
    await downloadBack();
    setDownloading(false);
  }, [downloadFront, downloadBack]);

  const canDownload = data.firstName.trim() && data.lastName.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[880px] max-h-[92vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2 text-[#1B365D]">
            <CreditCard className="w-5 h-5" />
            Business Card Generator
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
          {/* Form */}
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Fill in your details to generate a personalized business card.</p>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">First Name *</Label>
              <Input value={data.firstName} onChange={e => update('firstName', e.target.value)} placeholder="Malak" data-testid="bc-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Last Name *</Label>
              <Input value={data.lastName} onChange={e => update('lastName', e.target.value)} placeholder="Harb" data-testid="bc-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Title</Label>
              <Input value={data.title} onChange={e => update('title', e.target.value)} placeholder="Executive Manager" data-testid="bc-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Subtitle (optional)</Label>
              <Input value={data.subtitle} onChange={e => update('subtitle', e.target.value)} placeholder="Michigan Representative" data-testid="bc-subtitle" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Phone *</Label>
              <Input value={data.phone} onChange={e => update('phone', e.target.value)} placeholder="(313) 505-0586" data-testid="bc-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Email *</Label>
              <Input value={data.email} onChange={e => update('email', e.target.value)} placeholder="malak@ehiehr.com" data-testid="bc-email" />
            </div>
            <div className="flex flex-col gap-2 pt-3">
              <Button onClick={downloadBoth} disabled={downloading || !canDownload} className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full w-full" data-testid="bc-download-both">
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Generating...' : 'Download Both Sides'}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadFront} disabled={downloading || !canDownload} className="flex-1 rounded-full text-xs" data-testid="bc-download-front">
                  Front Only
                </Button>
                <Button variant="outline" size="sm" onClick={downloadBack} disabled={downloading} className="flex-1 rounded-full text-xs" data-testid="bc-download-back">
                  Back Only
                </Button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Live Preview</p>

            <div>
              <p className="text-[10px] text-slate-400 mb-1">Front</p>
              <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 bg-white" style={{ width: Math.round(CARD_W * PREVIEW_SCALE), height: Math.round(CARD_H * PREVIEW_SCALE) }}>
                <CardFrontPreview data={data} />
              </div>
            </div>

            <div>
              <p className="text-[10px] text-slate-400 mb-1">Back</p>
              <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200" style={{ width: Math.round(CARD_W * PREVIEW_SCALE), height: Math.round(CARD_H * PREVIEW_SCALE) }}>
                <CardBackPreview />
              </div>
            </div>

            <p className="text-[10px] text-slate-400 text-center">3.5" x 2" — Downloads as high-res PNG (300 DPI)</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
